/**
 * BS4IT Cybersec Assessment — Public Form
 *
 * 4 etapas:
 *  1. Perguntas (5 blocos temáticos com múltipla escolha + "Outro")
 *  2. Cadastro de servidores
 *  3. Cadastro de ativos SegInfo
 *  4. Dados cadastrais (CNPJ com lookup automático)
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  ChevronLeft, ChevronRight, CheckCircle2, Building2, Server,
  ShieldCheck, ClipboardList, Plus, Trash2, Network, Cpu, Users as UsersIcon,
  Radar, Activity,
} from 'lucide-react'
import AssessmentConsentStep, { ConsentValues } from '../components/AssessmentConsentStep'
import { GlossaryProvider, renderWithGlossary } from '../components/assessment/glossary'

const STORAGE_KEY = 'bs4it_draft_v2'
const API = '/api/v1/assessments/bs4it-cybersec/submit'

type DadosCadastrais = {
  razao_social: string
  cnpj: string
  inscricao_estadual: string
  endereco: string
  sponsor_nome: string
  sponsor_email: string
  telefone_fixo: string
  celular: string
  cargo: string
  cargo_outro: string
  setor: string
  porte: string
  urgencia: string
  consentimento_relatorio: boolean
  consentimento_contato: boolean
  consentimento_newsletter: boolean
}

const CARGO_OPTS = ['C-Level', 'Diretor', 'Gerente', 'Coordenador', 'Analista', 'Outro']
const SETOR_OPTS = [
  'Financeiro / Banking', 'Saúde', 'Energia / Utilities', 'Telecom', 'Governo',
  'Agronegócio', 'Indústria', 'Manufatura', 'Varejo', 'Logística',
  'Tecnologia', 'Educação', 'Construção', 'Outro',
]
const PORTE_OPTS = ['Até 100', '101 — 500', '501 — 2.000', '2.001 — 10.000', 'Mais de 10.000']
const URGENCIA_OPTS = ['Agora', '3 meses', '6 meses', '12 meses', 'Apenas avaliando']

const BLOCKED_EMAIL_DOMAINS = new Set([
  'gmail.com', 'hotmail.com', 'yahoo.com', 'yahoo.com.br',
  'outlook.com', 'live.com', 'icloud.com', 'msn.com',
  'bol.com.br', 'uol.com.br', 'terra.com.br', 'globo.com',
  'aol.com', 'mail.com', 'protonmail.com',
])

type ServidorRow = {
  unid: string; so: string; hostname: string; hypervisor: string
  fisico: string; virtual: string; ambiente: string; prioridade: string; ip: string
}
type AtivoRow = {
  unid: string; tipo: string; hostname: string; prioridade: string; ip: string
}

const PRIORIDADE_OPTS = ['Crítico', 'Alta', 'Média', 'Baixa']
const AMBIENTE_OPTS = ['PRD', 'QLD', 'DEV']

// Sistemas operacionais (agrupados por família)
const SO_OPTS: { label: string; options: string[] }[] = [
  { label: 'Linux', options: [
    'RHEL 9', 'RHEL 8', 'RHEL 7',
    'Oracle Linux 9', 'Oracle Linux 8',
    'Rocky Linux 9', 'Rocky Linux 8',
    'AlmaLinux 9', 'AlmaLinux 8',
    'Ubuntu Server 24.04', 'Ubuntu Server 22.04', 'Ubuntu Server 20.04',
    'Debian 12', 'Debian 11',
    'SUSE Linux Enterprise 15', 'SUSE Linux Enterprise 12',
    'CentOS 7', 'Amazon Linux 2023', 'Amazon Linux 2',
  ]},
  { label: 'Windows', options: [
    'Windows Server 2025', 'Windows Server 2022', 'Windows Server 2019',
    'Windows Server 2016', 'Windows Server 2012 R2', 'Windows Server 2008 R2',
  ]},
  { label: 'Unix / Legado', options: [
    'IBM AIX', 'HP-UX', 'Oracle Solaris', 'FreeBSD', 'OpenBSD', 'IBM i (AS/400)',
  ]},
  { label: 'Appliance / Virtualização', options: [
    'VMware ESXi', 'VMware vCenter', 'Citrix Hypervisor',
    'Nutanix AHV', 'Proxmox VE',
  ]},
  { label: 'Outros', options: ['Outro'] },
]

// Hypervisors mais comuns no mercado BR
const HYPERVISOR_OPTS = [
  'VMware vSphere/ESXi',
  'Microsoft Hyper-V',
  'Nutanix AHV',
  'Citrix Hypervisor (XenServer)',
  'KVM / QEMU',
  'Proxmox VE',
  'Oracle VM',
  'IBM PowerVM',
  'Outro',
]

const TIPO_SERVIDOR_OPTS = ['Físico', 'Virtual'] as const

// Tipos de ativos de SegInfo — opções padrão de mercado
const DEFAULT_TIPO_ATIVO_OPTS: string[] = [
  'Firewall NGFW',
  'Firewall UTM',
  'IPS / IDS',
  'WAF (Web Application Firewall)',
  'EDR / XDR',
  'Antivírus / Endpoint Protection',
  'SIEM',
  'SOAR',
  'DLP (Data Loss Prevention)',
  'CASB',
  'ZTNA / SASE',
  'VPN Concentrator',
  'Email Security Gateway',
  'Antispam',
  'Web Proxy / Web Security',
  'MFA / 2FA',
  'IAM / SSO',
  'PAM (Privileged Access Management)',
  'IGA (Identity Governance)',
  'HSM / Key Vault',
  'Network Access Control (NAC)',
  'Sandbox',
  'Vulnerability Scanner',
  'Patch Management',
  'Backup Security',
  'CDN / DDoS Protection',
  'PKI / Certificados',
]
const TIPO_ATIVO_CUSTOM_KEY = 'bs4it_custom_tipo_ativo'

function loadCustomTipoAtivo(): string[] {
  try {
    const raw = localStorage.getItem(TIPO_ATIVO_CUSTOM_KEY)
    if (raw) return JSON.parse(raw)
  } catch {/* ignore */}
  return []
}
function saveCustomTipoAtivo(list: string[]) {
  try { localStorage.setItem(TIPO_ATIVO_CUSTOM_KEY, JSON.stringify(list)) } catch {/* ignore */}
}

// ─── Definição das perguntas agrupadas por tema ─────────────────────────────

type QType = 'single' | 'multi'
type Question = {
  id: string
  label: string
  type: QType
  options: string[]
  allowOther?: boolean // se permite digitar "Outro"
}
type QuestionGroup = {
  id: string
  title: string
  subtitle: string
  icon: any
  questions: Question[]
}

const QUESTION_GROUPS: QuestionGroup[] = [
  {
    id: 'organizacao',
    title: 'Organização',
    subtitle: 'Porte e distribuição da operação',
    icon: UsersIcon,
    questions: [
      { id: 'q_colaboradores', label: 'Quantos colaboradores existem?', type: 'single',
        options: ['Até 50', '51 — 100', '101 — 500', '501 — 1.000', '1.001 — 5.000', 'Mais de 5.000'] },
      { id: 'q_usuarios_ti', label: 'Quantos usuários ativos de TI?', type: 'single',
        options: ['Até 50', '51 — 100', '101 — 500', '501 — 1.000', '1.001 — 5.000', 'Mais de 5.000'] },
      { id: 'q_filiais', label: 'Quantas filiais/unidades?', type: 'single',
        options: ['1 (matriz apenas)', '2 — 5', '6 — 10', '11 — 50', '51 — 100', 'Mais de 100'] },
      { id: 'q_operacoes_internacionais', label: 'Existem operações internacionais?', type: 'single',
        options: ['Sim', 'Não', 'Em planejamento'] },
    ],
  },
  {
    id: 'infraestrutura',
    title: 'Infraestrutura',
    subtitle: 'Ambiente computacional e topologia',
    icon: Network,
    questions: [
      { id: 'q_ambiente', label: 'Modelo de ambiente computacional', type: 'single',
        options: ['On-premises', 'Cloud', 'Híbrido'] },
      { id: 'q_clouds', label: 'Quais clouds são utilizadas?', type: 'multi',
        options: ['AWS', 'Microsoft Azure', 'Google Cloud (GCP)', 'Oracle Cloud (OCI)', 'IBM Cloud', 'Alibaba Cloud', 'Não utiliza', 'Outro'],
        allowOther: true },
      { id: 'q_endpoints', label: 'Quantos endpoints existem?', type: 'single',
        options: ['Até 100', '101 — 500', '501 — 1.000', '1.001 — 5.000', '5.001 — 10.000', 'Mais de 10.000'] },
      { id: 'q_servidores', label: 'Quantos servidores?', type: 'single',
        options: ['Até 20', '21 — 50', '51 — 100', '101 — 300', '301 — 1.000', 'Mais de 1.000'] },
      { id: 'q_dispositivos_rede', label: 'Quantos dispositivos de rede?', type: 'single',
        options: ['Até 10', '11 — 50', '51 — 100', '101 — 300', 'Mais de 300'] },
      { id: 'q_firewalls', label: 'Quantos firewalls?', type: 'single',
        options: ['1', '2 — 3', '4 — 10', '11 — 25', 'Mais de 25'] },
      { id: 'q_ot_ics', label: 'Existem ambientes OT/ICS (chão de fábrica, industrial)?', type: 'single',
        options: ['Sim', 'Não', 'Não sei informar'] },
      { id: 'q_containers_k8s', label: 'Existem workloads em containers/Kubernetes?', type: 'single',
        options: ['Sim — Kubernetes', 'Sim — Docker Swarm', 'Sim — OpenShift', 'Sim — Rancher', 'Não', 'Outro'],
        allowOther: true },
    ],
  },
  {
    id: 'apps',
    title: 'Aplicações e Ativos',
    subtitle: 'Sistemas críticos e classificação',
    icon: Cpu,
    questions: [
      { id: 'q_apps_criticas_internet', label: 'Existem aplicações críticas expostas à internet?', type: 'single',
        options: ['Sim — várias', 'Sim — poucas', 'Não', 'Não sei informar'] },
      { id: 'q_sistemas_criticos', label: 'Quais sistemas são considerados críticos?', type: 'multi',
        options: ['ERP', 'CRM', 'Core bancário / financeiro', 'eCommerce / Portais', 'BI / Data warehouse', 'Email corporativo', 'RH / Folha', 'Sistemas legados', 'Outro'],
        allowOther: true },
      { id: 'q_classificacao_ativos', label: 'Existe classificação de ativos?', type: 'single',
        options: ['Sim — formalizada', 'Sim — informal', 'Não', 'Em projeto'] },
      { id: 'q_cmdb', label: 'Existe CMDB atualizada?', type: 'single',
        options: ['Sim — atualizada', 'Sim — desatualizada', 'Não', 'Em implementação'] },
      { id: 'q_ambientes_segregados', label: 'Existem ambientes segregados?', type: 'single',
        options: ['Sim — PRD/QLD/DEV', 'Sim — DMZ', 'Sim — múltiplas zonas', 'Parcialmente', 'Não'] },
    ],
  },
  {
    id: 'time-seguranca',
    title: 'Time de Segurança',
    subtitle: 'Maturidade, processos e governança',
    icon: ShieldCheck,
    questions: [
      { id: 'q_equipe_seguranca', label: 'Existe equipe interna de segurança?', type: 'single',
        options: ['Sim — equipe dedicada', 'Sim — compartilhada com TI', 'Terceirizada (MSSP)', 'Não'] },
      { id: 'q_soc_atual', label: 'Existe SOC atual?', type: 'single',
        options: ['Sim — próprio (in-house)', 'Sim — terceirizado (MSSP/MDR)', 'Não', 'Em avaliação'] },
      { id: 'q_maturidade_atual', label: 'Qual o nível de maturidade atual?', type: 'single',
        options: ['Inicial (manual)', 'Básico (controles pontuais)', 'Intermediário (processos)', 'Avançado (orquestração)', 'Otimizado (ROC/ITDR)'] },
      { id: 'q_processo_incidentes', label: 'Existe processo formal de resposta a incidentes?', type: 'single',
        options: ['Sim — documentado e testado', 'Sim — documentado', 'Sim — informal', 'Não', 'Em desenvolvimento'] },
      { id: 'q_playbook', label: 'Existe playbook documentado?', type: 'single',
        options: ['Sim — múltiplos cenários', 'Sim — básico', 'Parcialmente', 'Não'] },
      { id: 'q_gestao_vulnerabilidades', label: 'Existe gestão de vulnerabilidades?', type: 'single',
        options: ['Sim — scan automatizado contínuo', 'Sim — scans periódicos', 'Sim — manual / ad-hoc', 'Não', 'Em implementação'] },
      { id: 'q_awareness', label: 'Existe programa de awareness?', type: 'single',
        options: ['Sim — contínuo', 'Sim — periódico', 'Sim — ad-hoc', 'Não', 'Em planejamento'] },
      { id: 'q_framework', label: 'Frameworks adotados', type: 'multi',
        options: ['NIST CSF', 'ISO 27001', 'CIS Controls', 'MITRE ATT&CK', 'LGPD', 'PCI-DSS', 'BACEN', 'COBIT', 'Nenhum', 'Outro'],
        allowOther: true },
    ],
  },
  {
    id: 'soc-siem',
    title: 'SOC e SIEM',
    subtitle: 'Operação de segurança, logs e integrações',
    icon: Radar,
    questions: [
      { id: 'q_siem', label: 'Já existe SIEM implantado?', type: 'single',
        options: ['Splunk', 'Microsoft Sentinel', 'IBM QRadar', 'Elastic SIEM', 'Wazuh', 'Sumo Logic', 'LogRhythm', 'AlienVault USM', 'Não tem', 'Outro'],
        allowOther: true },
      { id: 'q_volume_logs', label: 'Qual volume diário de logs?', type: 'single',
        options: ['Menos de 10 GB/dia', '10 — 50 GB/dia', '50 — 200 GB/dia', '200 — 500 GB/dia', '500 GB — 1 TB/dia', 'Mais de 1 TB/dia', 'Não sei informar'] },
      { id: 'q_retencao', label: 'Qual retenção de logs necessária?', type: 'single',
        options: ['30 dias', '90 dias', '6 meses', '1 ano', '3 anos', '5 anos', '7 anos', 'Não definido', 'Outro'],
        allowOther: true },
      { id: 'q_requisito_retencao', label: 'Existem requisitos regulatórios de retenção?', type: 'multi',
        options: ['LGPD', 'BACEN (BCB 4.658)', 'SOX', 'ISO 27001', 'PCI-DSS', 'ANVISA', 'ANEEL', 'ANATEL', 'Não tem', 'Outro'],
        allowOther: true },
      { id: 'q_integracoes', label: 'Integrações obrigatórias com o SIEM/SOC', type: 'multi',
        options: ['AD / Entra ID', 'Firewall', 'EDR / AV', 'Endpoints', 'Cloud (AWS/Azure/GCP)', 'Email / M365', 'WAF', 'VPN', 'Identity Provider (SSO)', 'Bancos de dados', 'Outro'],
        allowOther: true },
      { id: 'q_modelo_soc', label: 'Modelo desejado de operação', type: 'single',
        options: ['10x5 (horário comercial)', '14x5 (estendido)', '24x7', 'Não definido'] },
      { id: 'q_chamados_mes', label: 'Volume mensal de chamados', type: 'single',
        options: ['Até 50', '51 — 200', '201 — 500', '501 — 1.000', 'Mais de 1.000', 'Não sei informar'] },
      { id: 'q_incidentes_mes', label: 'Volume mensal de incidentes', type: 'single',
        options: ['Até 5', '6 — 20', '21 — 50', '51 — 100', 'Mais de 100', 'Não sei informar'] },
    ],
  },
]

const STEPS = [
  { id: 1, label: 'Perguntas',  icon: Activity },
  { id: 2, label: 'Servidores', icon: Server },
  { id: 3, label: 'Ativos',     icon: ShieldCheck },
  { id: 4, label: 'Dados',      icon: Building2 },
]

function emptyServidor(): ServidorRow {
  return { unid: '', so: '', hostname: '', hypervisor: '', fisico: '', virtual: '', ambiente: '', prioridade: '', ip: '' }
}
function emptyAtivo(): AtivoRow {
  return { unid: '', tipo: '', hostname: '', prioridade: '', ip: '' }
}

// Format CNPJ as user types: 00.000.000/0000-00
function formatCNPJ(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 14)
  if (d.length <= 2) return d
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`
}

export default function AssessmentBS4ITPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  // LGPD — tela de consentimento exibida ANTES das perguntas técnicas (step 1).
  // Enquanto `consentAccepted=false` mostramos somente <AssessmentConsentStep />.
  const [consentAccepted, setConsentAccepted] = useState(false)
  const [dados, setDados] = useState<DadosCadastrais>({
    razao_social: '', cnpj: '', inscricao_estadual: '', endereco: '',
    sponsor_nome: '', sponsor_email: '', telefone_fixo: '', celular: '',
    cargo: '', cargo_outro: '', setor: '', porte: '', urgencia: '',
    consentimento_relatorio: false,
    consentimento_contato: false,
    consentimento_newsletter: false,
  })
  const [servidores, setServidores] = useState<ServidorRow[]>([emptyServidor()])
  const [ativos, setAtivos] = useState<AtivoRow[]>([emptyAtivo()])
  const [informacoes, setInformacoes] = useState<Record<string, any>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ submission_id: string } | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [cnpjMessage, setCnpjMessage] = useState<{ text: string; ok: boolean } | null>(null)
  const lastLookedCnpj = useRef<string>('')

  // Tipos de ativos: defaults + customizações persistidas
  const [customTiposAtivo, setCustomTiposAtivo] = useState<string[]>(() => loadCustomTipoAtivo())
  const tipoAtivoOpts = [...DEFAULT_TIPO_ATIVO_OPTS, ...customTiposAtivo]
  const [pendingOtherAtivo, setPendingOtherAtivo] = useState<{ rowIdx: number } | null>(null)
  function addCustomTipoAtivo(name: string) {
    const trimmed = name.trim()
    if (!trimmed) return
    if (!tipoAtivoOpts.some(o => o.toLowerCase() === trimmed.toLowerCase())) {
      const next = [...customTiposAtivo, trimmed]
      setCustomTiposAtivo(next)
      saveCustomTipoAtivo(next)
    }
    if (pendingOtherAtivo) {
      setAtivos(prev => prev.map((r, i) => i === pendingOtherAtivo.rowIdx ? { ...r, tipo: trimmed } : r))
    }
    setPendingOtherAtivo(null)
  }

  async function handleCnpjLookup(rawCnpj: string) {
    const raw = rawCnpj.replace(/\D/g, '')
    if (raw.length !== 14) return
    if (lastLookedCnpj.current === raw) return  // evita lookup duplicado
    lastLookedCnpj.current = raw
    setCnpjLoading(true)
    setCnpjMessage(null)
    try {
      const { data } = await axios.post('/api/v1/assessments/lookup-cnpj', { cnpj: raw })
      if (!data.found) {
        setCnpjMessage({ text: 'CNPJ não localizado na Receita Federal', ok: false })
        return
      }
      setDados(prev => ({
        ...prev,
        cnpj: data.cnpj || prev.cnpj,
        razao_social: prev.razao_social || data.razao_social || data.nome_fantasia || '',
        endereco: prev.endereco || data.endereco_formatado || '',
        telefone_fixo: prev.telefone_fixo || data.telefone || '',
      }))
      setCnpjMessage({ text: `Dados de "${data.razao_social || data.nome_fantasia}" carregados`, ok: true })
    } catch (err: any) {
      setCnpjMessage({ text: err?.response?.data?.detail || 'Falha na consulta de CNPJ', ok: false })
    } finally {
      setCnpjLoading(false)
    }
  }

  // Restore draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const d = JSON.parse(raw)
        if (d.step) setStep(d.step)
        if (d.dados) setDados(d.dados)
        if (d.servidores?.length) setServidores(d.servidores)
        if (d.ativos?.length) setAtivos(d.ativos)
        if (d.informacoes) setInformacoes(d.informacoes)
        // Se o draft já tinha consentimento_relatorio (sessão anterior), pula a tela LGPD.
        if (d.dados?.consentimento_relatorio === true) setConsentAccepted(true)
      }
    } catch {/* ignore */}
  }, [])

  useEffect(() => {
    if (done) return
    const t = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, dados, servidores, ativos, informacoes }))
    }, 600)
    return () => clearTimeout(t)
  }, [step, dados, servidores, ativos, informacoes, done])

  /** Aceita o consentimento LGPD e libera o wizard (step 1+). */
  function handleConsentAccept(consents: ConsentValues) {
    setDados(prev => ({
      ...prev,
      consentimento_relatorio: consents.relatorio,
      consentimento_contato: consents.contato,
      consentimento_newsletter: consents.newsletter,
    }))
    setConsentAccepted(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function validateStep(): { errors: Record<string, string>; firstFieldId: string | null } {
    const errors: Record<string, string> = {}
    let firstFieldId: string | null = null
    if (step === 4) {
      if (!dados.razao_social.trim()) { errors['razao_social'] = 'Obrigatório'; firstFieldId = firstFieldId || 'razao_social' }
      if (!dados.cnpj.trim() || dados.cnpj.replace(/\D/g, '').length !== 14) {
        errors['cnpj'] = 'CNPJ deve ter 14 dígitos'; firstFieldId = firstFieldId || 'cnpj'
      }
      if (!dados.sponsor_nome.trim()) { errors['sponsor_nome'] = 'Obrigatório'; firstFieldId = firstFieldId || 'sponsor_nome' }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dados.sponsor_email)) {
        errors['sponsor_email'] = 'E-mail inválido'; firstFieldId = firstFieldId || 'sponsor_email'
      } else {
        const domain = dados.sponsor_email.toLowerCase().split('@')[1]
        if (BLOCKED_EMAIL_DOMAINS.has(domain)) {
          errors['sponsor_email'] = `Use seu e-mail corporativo (${domain} não é aceito)`
          firstFieldId = firstFieldId || 'sponsor_email'
        }
      }
      if (!dados.cargo) {
        errors['cargo'] = 'Obrigatório'; firstFieldId = firstFieldId || 'cargo'
      } else if (dados.cargo === 'Outro' && !dados.cargo_outro.trim()) {
        errors['cargo'] = 'Especifique seu cargo'; firstFieldId = firstFieldId || 'cargo'
      }
      if (!dados.setor) { errors['setor'] = 'Obrigatório'; firstFieldId = firstFieldId || 'setor' }
      if (!dados.porte) { errors['porte'] = 'Obrigatório'; firstFieldId = firstFieldId || 'porte' }
      if (!dados.urgencia) { errors['urgencia'] = 'Obrigatório'; firstFieldId = firstFieldId || 'urgencia' }
      // consentimento_relatorio é validado na step 0 (AssessmentConsentStep) — não revalidamos aqui.
    }
    return { errors, firstFieldId }
  }

  function focusField(fid: string) {
    setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`[data-field-id="${fid}"]`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const input = el.querySelector<HTMLElement>('input, select, textarea')
      if (input) input.focus({ preventScroll: true })
    }, 50)
  }

  function handleNext() {
    const { errors, firstFieldId } = validateStep()
    setValidationErrors(errors)
    if (Object.keys(errors).length > 0) {
      if (firstFieldId) focusField(firstFieldId)
      return
    }
    setValidationErrors({})
    setStep(s => Math.min(STEPS.length, s + 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function handleSubmit() {
    const { errors, firstFieldId } = validateStep()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      if (firstFieldId) focusField(firstFieldId)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const cleanServidores = servidores.filter(r => Object.values(r).some(v => v.trim()))
      const cleanAtivos = ativos.filter(r => Object.values(r).some(v => v.trim()))
      // Se cargo='Outro' com texto, substitui pelo customizado mantendo o original em cargo_outro
      const dadosFinal = (dados.cargo === 'Outro' && dados.cargo_outro.trim())
        ? { ...dados, cargo: dados.cargo_outro.trim() }
        : dados
      const { data } = await axios.post(API, {
        dados_cadastrais: dadosFinal,
        servidores: cleanServidores,
        ativos: cleanAtivos,
        informacoes,
      })
      localStorage.removeItem(STORAGE_KEY)
      // Navega para página de resultado com score
      navigate(`/assessment/bs4it/resultado/${data.submission_id}`)
      return
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao enviar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-6">
          <CheckCircle2 className="h-20 w-20 text-emerald-400 mx-auto" />
          <h1 className="text-3xl font-bold">Recebemos seu assessment!</h1>
          <p className="text-slate-300">A BS4IT entrará em contato em até 2 dias úteis para apresentar a proposta.</p>
          <p className="text-xs text-slate-500">ID da submissão: {done.submission_id.slice(0, 8)}…</p>
          <button onClick={() => navigate('/assessments')}
            className="px-6 py-2.5 rounded-full bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-500 transition">
            Voltar para diagnósticos
          </button>
        </div>
      </div>
    )
  }

  return (
    <GlossaryProvider>
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-4 py-5">
          <div className="flex items-center justify-center gap-6">
            <img
              src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png"
              alt="NAI"
              style={{ height: '56px', width: 'auto' }}
            />
            <span className="text-3xl font-light text-slate-400 leading-none">&</span>
            <img
              src="https://social.myncc.com.br/landingpages/assets/bs4it-logo.png"
              alt="BS4IT"
              style={{ height: '56px', width: 'auto' }}
            />
          </div>
          <p className="text-center text-sm text-slate-300 mt-3 italic">
            Juntas promovendo a blindagem lógica e regulatória da sua empresa.
          </p>
          <p className="text-center text-[10px] uppercase tracking-widest text-cyan-400/70 mt-2">
            Assessment Cibersegurança · ~20 min
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* LGPD — Step 0 (consentimento). Bloqueia o wizard até o consent obrigatório ser marcado. */}
        {!consentAccepted ? (
          <AssessmentConsentStep
            partner="BS4IT"
            initial={{
              relatorio: dados.consentimento_relatorio,
              contato: dados.consentimento_contato,
              newsletter: dados.consentimento_newsletter,
            }}
            onAccept={handleConsentAccept}
          />
        ) : (
        <>
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-xs">
            {STEPS.map((s, i) => {
              const SIcon = s.icon
              const active = step === s.id
              const stepDone = step > s.id
              return (
                <li key={s.id} className="flex items-center gap-2 flex-1">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold transition ${
                    active ? 'bg-cyan-500/20 border border-cyan-500/40 text-cyan-200'
                    : stepDone ? 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-200'
                    : 'bg-slate-800/60 border border-slate-700 text-slate-500'
                  }`}>
                    <SIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="h-px flex-1 bg-slate-700" />}
                </li>
              )
            })}
          </ol>
        </nav>

        {Object.keys(validationErrors).length > 0 && (
          <div className="mb-6 p-4 rounded-xl bg-red-900/30 border border-red-700/60 text-red-200 text-sm flex items-start gap-3">
            <span className="text-xl leading-none">⚠️</span>
            <div>
              <div className="font-bold text-red-100 mb-0.5">Faltam campos obrigatórios</div>
              <div className="text-red-300/90 text-xs">Os campos destacados em vermelho precisam ser preenchidos.</div>
            </div>
          </div>
        )}

        {step === 1 && (
          <StepPerguntas values={informacoes} onChange={setInformacoes} />
        )}
        {step === 2 && (
          <Step2Servidores rows={servidores} onChange={setServidores} />
        )}
        {step === 3 && (
          <Step3Ativos
            rows={ativos}
            onChange={setAtivos}
            tipoOpts={tipoAtivoOpts}
            onPickOther={(rowIdx) => setPendingOtherAtivo({ rowIdx })}
          />
        )}
        {step === 4 && (
          <StepDados
            dados={dados} onChange={setDados} errors={validationErrors}
            cnpjLoading={cnpjLoading} cnpjMessage={cnpjMessage}
            onCnpjChange={(formatted) => {
              setDados(prev => ({ ...prev, cnpj: formatted }))
              const raw = formatted.replace(/\D/g, '')
              if (raw.length === 14) handleCnpjLookup(raw)
              else if (raw.length < 14) {
                setCnpjMessage(null)
                lastLookedCnpj.current = ''
              }
            }}
          />
        )}

        {error && (
          <div className="mt-4 p-3 rounded-md bg-red-900/30 border border-red-700/60 text-red-200 text-sm">{error}</div>
        )}

        <div className="mt-10 flex items-center justify-between gap-3">
          <button type="button"
            onClick={() => { setValidationErrors({}); setStep(s => Math.max(1, s - 1)); window.scrollTo({top:0, behavior:'smooth'}) }}
            disabled={step === 1}
            className="px-5 py-2.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800/60 disabled:opacity-30 disabled:cursor-not-allowed transition text-sm font-semibold inline-flex items-center gap-1.5"
          >
            <ChevronLeft className="h-4 w-4" /> Voltar
          </button>
          <div className="text-xs text-slate-500">Etapa {step}/{STEPS.length}</div>
          {step < STEPS.length ? (
            <button type="button" onClick={handleNext}
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-bold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] transition inline-flex items-center gap-1.5">
              Continuar <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button type="button" onClick={handleSubmit} disabled={submitting}
              className="px-6 py-2.5 rounded-full bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition">
              {submitting ? 'Enviando…' : 'Concluir e enviar para BS4IT'}
            </button>
          )}
        </div>
        </>
        )}
      </main>

      {pendingOtherAtivo && (
        <OtherTipoAtivoModal
          onCancel={() => setPendingOtherAtivo(null)}
          onConfirm={addCustomTipoAtivo}
        />
      )}
    </div>
    </GlossaryProvider>
  )
}

// ─── Modal "Outro tipo de ativo" ────────────────────────────────────────────

function OtherTipoAtivoModal({ onCancel, onConfirm }: {
  onCancel: () => void
  onConfirm: (name: string) => void
}) {
  const [name, setName] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 className="text-lg font-bold text-white">Novo tipo de ativo</h3>
          <p className="text-xs text-slate-400 mt-1">
            Cadastre um tipo que ainda não está na lista. Ele será adicionado às opções padrão e pode ser reutilizado nas próximas linhas.
          </p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-1.5">
            Tipo do ativo
          </label>
          <input type="text" value={name} autoFocus
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onConfirm(name) }}
            placeholder="Ex.: NDR · Honeypot · Cyber Insurance Platform"
            className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel}
            className="px-4 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm font-semibold">
            Cancelar
          </button>
          <button type="button" disabled={!name.trim()}
            onClick={() => onConfirm(name)}
            className="px-4 py-2 rounded-md bg-cyan-600 text-white text-sm font-bold hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed">
            Cadastrar e selecionar
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Step 1: Perguntas agrupadas ────────────────────────────────────────────

function StepPerguntas({ values, onChange }: {
  values: Record<string, any>
  onChange: (v: Record<string, any>) => void
}) {
  const update = (k: string, v: any) => onChange({ ...values, [k]: v })
  const totalQs = QUESTION_GROUPS.reduce((s, g) => s + g.questions.length, 0)
  const answeredQs = QUESTION_GROUPS.reduce((s, g) => s + g.questions.filter(q => {
    const v = values[q.id]
    if (q.type === 'multi') return Array.isArray(v) && v.length > 0
    return v && String(v).trim()
  }).length, 0)

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Activity className="h-6 w-6 text-cyan-400" /> Perguntas técnicas
        </h2>
        <p className="text-sm text-slate-400 mt-1">
          Responda livremente — opções padrão de mercado pré-listadas. Use <strong>"Outro"</strong> quando faltar uma opção.
        </p>
        <div className="mt-3 text-xs text-slate-500">
          <strong className="text-cyan-300">{answeredQs}</strong> de {totalQs} perguntas respondidas
        </div>
      </div>

      {QUESTION_GROUPS.map((group) => {
        const GIcon = group.icon
        const blockAnswered = group.questions.filter(q => {
          const v = values[q.id]
          if (q.type === 'multi') return Array.isArray(v) && v.length > 0
          return v && String(v).trim()
        }).length
        return (
          <section key={group.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <GIcon className="h-5 w-5 text-cyan-400" />
                <h3 className="text-lg font-bold text-white">{group.title}</h3>
              </div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                {blockAnswered}/{group.questions.length}
              </span>
            </div>
            <p className="text-xs text-slate-400 mb-5">{group.subtitle}</p>

            <div className="space-y-5">
              {group.questions.map((q) => (
                <QuestionField key={q.id} q={q} value={values[q.id]} otherValue={values[`${q.id}_other`] || ''}
                  onChange={(v) => update(q.id, v)}
                  onOtherChange={(v) => update(`${q.id}_other`, v)} />
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

function QuestionField({ q, value, otherValue, onChange, onOtherChange }: {
  q: Question
  value: any
  otherValue: string
  onChange: (v: any) => void
  onOtherChange: (v: string) => void
}) {
  const isOtherToken = (s: string) => /^outro$/i.test((s || '').trim())

  if (q.type === 'single') {
    const sel = value || ''
    const isOtherSelected = isOtherToken(sel)
    return (
      <div data-field-id={q.id}>
        <label className="block text-sm font-semibold text-slate-200 mb-2">{renderWithGlossary(q.label)}</label>
        <div className="flex flex-wrap gap-2">
          {q.options.map((o) => {
            const active = sel === o
            return (
              <button key={o} type="button"
                onClick={() => {
                  onChange(o)
                  if (!isOtherToken(o) && otherValue) onOtherChange('')
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  active
                    ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                    : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                }`}>
                {o}
              </button>
            )
          })}
        </div>
        {isOtherSelected && q.allowOther && (
          <OtherInput value={otherValue} onChange={onOtherChange} />
        )}
      </div>
    )
  }

  // multi
  const sel: string[] = Array.isArray(value) ? value : []
  const isOtherSelected = sel.some(isOtherToken)
  return (
    <div data-field-id={q.id}>
      <label className="block text-sm font-semibold text-slate-200 mb-2">{q.label}</label>
      <div className="flex flex-wrap gap-2">
        {q.options.map((o) => {
          const active = sel.includes(o)
          return (
            <button key={o} type="button"
              onClick={() => {
                const next = active ? sel.filter(x => x !== o) : [...sel, o]
                onChange(next)
                if (active && isOtherToken(o) && otherValue) onOtherChange('')
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active
                  ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                  : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}>
              {o}
            </button>
          )
        })}
      </div>
      {isOtherSelected && q.allowOther && (
        <OtherInput value={otherValue} onChange={onOtherChange} />
      )}
    </div>
  )
}

function OtherInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="mt-3 pl-3 border-l-2 border-cyan-500/50">
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-cyan-300 mb-1.5">
        Descreva o "Outro"
      </label>
      <input type="text" value={value} placeholder="Especifique aqui…"
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-cyan-700/60 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-500"
      />
    </div>
  )
}

// ─── Step 2: Servidores ─────────────────────────────────────────────────────

function Step2Servidores({ rows, onChange }: { rows: ServidorRow[]; onChange: (r: ServidorRow[]) => void }) {
  const updatePatch = (idx: number, patch: Partial<ServidorRow>) =>
    onChange(rows.map((r, i) => i === idx ? { ...r, ...patch } : r))

  const setTipo = (idx: number, tipo: 'Físico' | 'Virtual') => {
    if (tipo === 'Físico') updatePatch(idx, { fisico: 'X', virtual: '', hypervisor: '' })
    else updatePatch(idx, { virtual: 'X', fisico: '' })
  }
  const getTipo = (row: ServidorRow): '' | 'Físico' | 'Virtual' =>
    row.fisico ? 'Físico' : row.virtual ? 'Virtual' : ''

  const addRow = () => onChange([...rows, emptyServidor()])
  const removeRow = (idx: number) =>
    onChange(rows.length > 1 ? rows.filter((_, i) => i !== idx) : [emptyServidor()])

  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Server className="h-6 w-6 text-cyan-400" /> Cadastro de servidores
      </h2>
      <p className="text-sm text-slate-400">
        Servidores e equipamentos a serem monitorados. Adicione quantos forem necessários.
      </p>

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/40">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-2 py-2">Unid.</th>
              <th className="text-left px-2 py-2 min-w-[180px]">Sistema Operacional</th>
              <th className="text-left px-2 py-2">Hostname</th>
              <th className="text-left px-2 py-2 min-w-[150px]">Tipo</th>
              <th className="text-left px-2 py-2 min-w-[180px]">Hypervisor</th>
              <th className="text-left px-2 py-2">Ambiente</th>
              <th className="text-left px-2 py-2">Prioridade</th>
              <th className="text-left px-2 py-2">IP</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const tipo = getTipo(row)
              return (
                <tr key={idx} className="border-t border-slate-800 align-top">
                  <td className="p-1"><CellInput value={row.unid} onChange={(v) => updatePatch(idx, { unid: v })} /></td>
                  <td className="p-1">
                    <CellGroupedSelect value={row.so} onChange={(v) => updatePatch(idx, { so: v })} groups={SO_OPTS} />
                  </td>
                  <td className="p-1"><CellInput value={row.hostname} onChange={(v) => updatePatch(idx, { hostname: v })} /></td>
                  <td className="p-1">
                    <div className="inline-flex rounded-md overflow-hidden border border-slate-700">
                      {TIPO_SERVIDOR_OPTS.map((t) => {
                        const active = tipo === t
                        return (
                          <button key={t} type="button" onClick={() => setTipo(idx, t)}
                            className={`px-2.5 py-1.5 text-[11px] font-semibold transition ${
                              active
                                ? 'bg-cyan-500/30 text-cyan-100'
                                : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                            }`}>
                            {t}
                          </button>
                        )
                      })}
                    </div>
                  </td>
                  <td className="p-1">
                    <CellSelect
                      value={row.hypervisor}
                      onChange={(v) => updatePatch(idx, { hypervisor: v })}
                      options={HYPERVISOR_OPTS}
                      disabled={tipo !== 'Virtual'}
                      placeholder={tipo === 'Virtual' ? '— escolha —' : 'N/A'}
                    />
                  </td>
                  <td className="p-1"><CellSelect value={row.ambiente} onChange={(v) => updatePatch(idx, { ambiente: v })} options={AMBIENTE_OPTS} /></td>
                  <td className="p-1"><CellSelect value={row.prioridade} onChange={(v) => updatePatch(idx, { prioridade: v })} options={PRIORIDADE_OPTS} /></td>
                  <td className="p-1"><CellInput value={row.ip} onChange={(v) => updatePatch(idx, { ip: v })} /></td>
                  <td className="p-1 text-center">
                    <button type="button" onClick={() => removeRow(idx)} className="text-slate-500 hover:text-red-400" title="Remover linha">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <button type="button" onClick={addRow}
        className="px-4 py-2 rounded-md border border-cyan-700/50 text-cyan-300 hover:bg-cyan-900/30 text-sm font-semibold inline-flex items-center gap-1.5">
        <Plus className="h-4 w-4" /> Adicionar servidor
      </button>
    </section>
  )
}

// ─── Step 3: Ativos SegInfo ─────────────────────────────────────────────────

function Step3Ativos({ rows, onChange, tipoOpts, onPickOther }: {
  rows: AtivoRow[]
  onChange: (r: AtivoRow[]) => void
  tipoOpts: string[]
  onPickOther: (rowIdx: number) => void
}) {
  const update = (idx: number, k: keyof AtivoRow, v: string) =>
    onChange(rows.map((r, i) => i === idx ? { ...r, [k]: v } : r))
  const addRow = () => onChange([...rows, emptyAtivo()])
  const removeRow = (idx: number) =>
    onChange(rows.length > 1 ? rows.filter((_, i) => i !== idx) : [emptyAtivo()])

  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-cyan-400" /> Cadastro de ativos de Segurança da Informação
      </h2>
      <p className="text-sm text-slate-400">
        Firewalls, IPS, WAF, EDR, SIEM, DLP, gateways, MFA, IAM e demais ativos de SegInfo.
        <span className="text-cyan-400"> Selecione "Outro" no campo Tipo para cadastrar um item fora do catálogo.</span>
      </p>

      <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/40">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400">
            <tr>
              <th className="text-left px-2 py-2">Unid.</th>
              <th className="text-left px-2 py-2 min-w-[220px]">Tipo</th>
              <th className="text-left px-2 py-2">Hostname</th>
              <th className="text-left px-2 py-2">Prioridade</th>
              <th className="text-left px-2 py-2">IP</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-t border-slate-800 align-top">
                <td className="p-1"><CellInput value={row.unid} onChange={(v) => update(idx, 'unid', v)} /></td>
                <td className="p-1">
                  <select
                    value={row.tipo}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === '__OTHER__') {
                        onPickOther(idx)
                      } else {
                        update(idx, 'tipo', v)
                      }
                    }}
                    className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 text-xs"
                  >
                    <option value="">— selecione —</option>
                    {tipoOpts.map(o => <option key={o} value={o}>{o}</option>)}
                    <option value="__OTHER__" className="font-bold text-cyan-300">+ Outro (cadastrar)…</option>
                  </select>
                </td>
                <td className="p-1"><CellInput value={row.hostname} onChange={(v) => update(idx, 'hostname', v)} /></td>
                <td className="p-1"><CellSelect value={row.prioridade} onChange={(v) => update(idx, 'prioridade', v)} options={PRIORIDADE_OPTS} /></td>
                <td className="p-1"><CellInput value={row.ip} onChange={(v) => update(idx, 'ip', v)} /></td>
                <td className="p-1 text-center">
                  <button type="button" onClick={() => removeRow(idx)} className="text-slate-500 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button type="button" onClick={addRow}
        className="px-4 py-2 rounded-md border border-cyan-700/50 text-cyan-300 hover:bg-cyan-900/30 text-sm font-semibold inline-flex items-center gap-1.5">
        <Plus className="h-4 w-4" /> Adicionar ativo
      </button>
    </section>
  )
}

// ─── Step 4: Dados cadastrais (CNPJ auto-lookup) ────────────────────────────

function StepDados({ dados, onChange, errors, cnpjLoading, cnpjMessage, onCnpjChange }: {
  dados: DadosCadastrais
  onChange: (d: DadosCadastrais) => void
  errors: Record<string, string>
  cnpjLoading: boolean
  cnpjMessage: { text: string; ok: boolean } | null
  onCnpjChange: (formatted: string) => void
}) {
  const set = (k: keyof DadosCadastrais) => (v: string) => onChange({ ...dados, [k]: v })

  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Building2 className="h-6 w-6 text-cyan-400" /> Dados cadastrais
      </h2>
      <p className="text-sm text-slate-400">
        Comece pelo CNPJ — a Razão Social, endereço e telefone serão preenchidos automaticamente.
      </p>

      {/* CNPJ com auto-lookup */}
      <div data-field-id="cnpj">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
          CNPJ <span className="text-red-400 ml-1">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={dados.cnpj}
            placeholder="00.000.000/0000-00"
            inputMode="numeric"
            maxLength={18}
            onChange={(e) => onCnpjChange(formatCNPJ(e.target.value))}
            className={`w-full px-3 py-2.5 pr-10 rounded-lg bg-slate-800 border text-slate-100 focus:outline-none focus:ring-2 text-sm font-mono ${
              errors['cnpj'] ? 'border-red-500/70 focus:ring-red-500' : 'border-slate-700 focus:ring-cyan-500'
            }`}
          />
          {cnpjLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin"></div>
            </div>
          )}
          {!cnpjLoading && cnpjMessage?.ok && (
            <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
          )}
        </div>
        {errors['cnpj'] && <p className="mt-1.5 text-xs text-red-400 font-semibold">{errors['cnpj']}</p>}
        {cnpjMessage && (
          <p className={`mt-1.5 text-xs font-semibold ${cnpjMessage.ok ? 'text-emerald-400' : 'text-amber-400'}`}>
            {cnpjMessage.ok ? '✓ ' : ''}{cnpjMessage.text}
          </p>
        )}
        <p className="text-[10px] text-slate-500 mt-1">A consulta dispara automaticamente ao preencher 14 dígitos.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Field label="Razão Social *" required value={dados.razao_social} onChange={set('razao_social')}
          fieldId="razao_social" error={errors['razao_social']} />

        {/* Inscrição Estadual com botão ISENTO */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
            Inscrição Estadual
          </label>
          <div className="flex gap-2">
            <input type="text" value={dados.inscricao_estadual}
              onChange={(e) => set('inscricao_estadual')(e.target.value)}
              placeholder="000.000.000.000 ou ISENTO"
              className="flex-1 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm" />
            <button type="button"
              onClick={() => set('inscricao_estadual')(dados.inscricao_estadual === 'ISENTO' ? '' : 'ISENTO')}
              className={`px-3 py-2.5 rounded-lg text-xs font-bold border transition ${
                dados.inscricao_estadual === 'ISENTO'
                  ? 'bg-amber-500/20 border-amber-400 text-amber-200'
                  : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500'
              }`}>
              ISENTO
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">A consulta CNPJ não retorna IE automaticamente — informe manualmente.</p>
        </div>

        <div className="sm:col-span-2">
          <Field label="Endereço" value={dados.endereco} onChange={set('endereco')} />
        </div>
        <Field label="Nome completo (Sponsor) *" required value={dados.sponsor_nome} onChange={set('sponsor_nome')}
          fieldId="sponsor_nome" error={errors['sponsor_nome']} />
        <Field label="E-mail corporativo *" required type="email" value={dados.sponsor_email} onChange={set('sponsor_email')}
          fieldId="sponsor_email" error={errors['sponsor_email']} />
        <Field label="Telefone fixo" value={dados.telefone_fixo} onChange={set('telefone_fixo')} />
        <Field label="Celular / WhatsApp" value={dados.celular} onChange={set('celular')} placeholder="+55 11 99999-9999" />
      </div>

      {/* BANT — qualificação obrigatória */}
      <div className="mt-6 pt-6 border-t border-slate-800">
        <h3 className="text-lg font-bold text-white mb-1">Qualificação BANT</h3>
        <p className="text-xs text-slate-400 mb-4">Informações que ajudam a BS4IT a calibrar a proposta.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <PillSelect label="Qual o seu cargo? *" value={dados.cargo} onChange={set('cargo')}
            options={CARGO_OPTS} fieldId="cargo" error={errors['cargo']}
            otherValue={dados.cargo_outro} onOtherChange={set('cargo_outro')}
            otherPlaceholder="Ex.: CFO · COO · DPO · Head de TI…" />
          <PillSelect label="Setor *" value={dados.setor} onChange={set('setor')}
            options={SETOR_OPTS} fieldId="setor" error={errors['setor']} />
          <PillSelect label="Porte (colaboradores) *" value={dados.porte} onChange={set('porte')}
            options={PORTE_OPTS} fieldId="porte" error={errors['porte']} />
          <PillSelect label="Horizonte para avaliar SOC/SIEM *" value={dados.urgencia} onChange={set('urgencia')}
            options={URGENCIA_OPTS} fieldId="urgencia" error={errors['urgencia']} />
        </div>
      </div>

      {/* LGPD — consentimentos coletados na Step 0 (AssessmentConsentStep).
          Mantido aqui apenas um lembrete discreto. */}
      <div className="mt-6 pt-6 border-t border-slate-800">
        <p className="text-[11px] text-slate-500">
          Consentimentos LGPD registrados no início desta avaliação. Para revisar ou
          revogar, contate <a href="mailto:dpo@nai-it.com" className="text-cyan-300 hover:text-cyan-200 underline">dpo@nai-it.com</a>.
        </p>
      </div>
    </section>
  )
}

function PillSelect({ label, value, onChange, options, fieldId, error,
                     otherValue, onOtherChange, otherPlaceholder }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
  fieldId?: string
  error?: string
  otherValue?: string
  onOtherChange?: (v: string) => void
  otherPlaceholder?: string
}) {
  const isOther = /^outro$/i.test(value || '')
  return (
    <div data-field-id={fieldId}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {options.map(o => {
          const active = value === o
          return (
            <button key={o} type="button"
              onClick={() => {
                onChange(o)
                if (!/^outro$/i.test(o) && onOtherChange && otherValue) onOtherChange('')
              }}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                active
                  ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                  : error
                    ? 'bg-slate-800/60 border-red-500/40 text-slate-400 hover:border-red-400'
                    : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}>
              {o}
            </button>
          )
        })}
      </div>
      {isOther && onOtherChange && (
        <div className="mt-2 pl-3 border-l-2 border-cyan-500/50">
          <input type="text" value={otherValue || ''}
            placeholder={otherPlaceholder || 'Especifique aqui…'}
            onChange={(e) => onOtherChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-cyan-700/60 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm placeholder-slate-500" />
        </div>
      )}
      {error && <p className="mt-1.5 text-xs text-red-400 font-semibold">{error}</p>}
    </div>
  )
}

function ConsentBox({ checked, onChange, label, required, error, fieldId }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  required?: boolean
  error?: string
  fieldId?: string
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-field-id={fieldId}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-950" />
      <div className="flex-1">
        <span className={`text-xs leading-relaxed ${error ? 'text-red-300' : 'text-slate-300 group-hover:text-slate-200'}`}>
          {label}{required && <span className="text-red-400 ml-1">*</span>}
        </span>
        {error && <p className="mt-1 text-[10px] text-red-400 font-semibold">{error}</p>}
      </div>
    </label>
  )
}

// ─── UI primitives ──────────────────────────────────────────────────────────

function Field({ label, value, onChange, required, type = 'text', placeholder, fieldId, error }: {
  label: string; value: string; onChange: (v: string) => void; required?: boolean
  type?: string; placeholder?: string; fieldId?: string; error?: string
}) {
  return (
    <div data-field-id={fieldId}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-lg bg-slate-800 border text-slate-100 focus:outline-none focus:ring-2 text-sm ${
          error ? 'border-red-500/70 focus:ring-red-500' : 'border-slate-700 focus:ring-cyan-500'
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-red-400 font-semibold">{error}</p>}
    </div>
  )
}

function CellInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 text-xs"
    />
  )
}

function CellSelect({ value, onChange, options, disabled, placeholder }: {
  value: string; onChange: (v: string) => void; options: string[]
  disabled?: boolean; placeholder?: string
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      className={`w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 text-xs ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      }`}>
      <option value="">{placeholder || ''}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

function CellGroupedSelect({ value, onChange, groups }: {
  value: string
  onChange: (v: string) => void
  groups: { label: string; options: string[] }[]
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 text-xs">
      <option value="">— selecione —</option>
      {groups.map(g => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map(o => <option key={o} value={o}>{o}</option>)}
        </optgroup>
      ))}
    </select>
  )
}
